import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Drama, Plus, Loader2, Trash2, Calendar, MapPin, Globe, IdCard,
  Users, ArrowRight, BadgeCheck, Theater, Quote, ExternalLink, Share2,
} from "lucide-react";
import OrgTeamPanel from "@/components/OrgTeamPanel";
import HeroImageGenerator from "@/components/HeroImageGenerator";

export default function TheaterGroupDashboard() {
  const [org, setOrg] = useState(null);
  const [productions, setProductions] = useState([]);
  const [loadingProds, setLoadingProds] = useState(true);
  const [bookings, setBookings] = useState({ count: 0, bookings: [], counts: {} });
  const [showAdd, setShowAdd] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [styleCatalog, setStyleCatalog] = useState([]);

  const accent = "#F97316";

  useEffect(() => {
    api.get("/onboard/me/organization")
      .then(({ data }) => setOrg(data.organization))
      .catch(() => setOrg(null));
    api.get("/meta/theater-styles")
      .then(({ data }) => setStyleCatalog(Array.isArray(data) ? data : []))
      .catch(() => {});
    refresh();
  }, []);

  const refresh = () => {
    setLoadingProds(true);
    api.get("/theater-group/productions/mine")
      .then(({ data }) => setProductions(data.productions || []))
      .catch(() => setProductions([]))
      .finally(() => setLoadingProds(false));
    api.get("/theater-group/bookings/mine")
      .then(({ data }) => setBookings(data || { count: 0, bookings: [], counts: {} }))
      .catch(() => {});
  };

  if (!org) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-16 text-center text-white/55"
           data-testid="theater-group-loading">Loading dashboard…</div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="theater-group-dashboard">
      {/* HERO */}
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
        L17 · Theater Group
      </div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2"
          style={{ color: accent }}
          data-testid="theater-group-brand">
        {org.brand_name || org.legal_name}
      </h1>
      <div className="mt-2 text-sm text-white/55 flex items-center gap-3 flex-wrap">
        {org.legal_name && org.legal_name !== org.brand_name && (
          <span>{org.legal_name}</span>
        )}
        {org.verified && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-emerald-500/15 text-emerald-300">
            <BadgeCheck size={10} className="inline mr-1"/> Verified
          </span>
        )}
        {org.registered_city && (
          <span className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase bg-white/8 text-white/65">
            <MapPin size={10} className="inline mr-1"/> {org.registered_city}
          </span>
        )}
        {(org.theater_styles || []).map((s) => {
          const label = styleCatalog.find((x) => x.key === s)?.label || s;
          return (
            <span key={s} className="px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase"
                  style={{ background: `${accent}22`, color: accent }}
                  data-testid={`theater-group-style-${s}`}>{label}</span>
          );
        })}
      </div>
      <p className="mt-4 text-sm text-white/60 max-w-2xl leading-relaxed">
        Your slate of plays, rehearsals, cast lists, and venue bookings — all here.
        Discover halls, request shows, message venues without leaving the platform.
      </p>

      {/* PUBLIC PROFILE ACTIONS */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="theater-group-profile-actions">
        {org.public_handle && (
          <>
            <Link to={`/theater-group/${org.public_handle}`} target="_blank"
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/8 hover:bg-white/15 text-white transition"
                  data-testid="theater-group-view-public">
              <ExternalLink size={11}/> View Public Profile
            </Link>
            <button onClick={() => {
                const url = `${window.location.origin}/theater-group/${org.public_handle}`;
                navigator.clipboard.writeText(url).then(() => toast.success("Public link copied."));
              }}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/8 hover:bg-white/15 text-white transition"
              data-testid="theater-group-copy-link">
              <Share2 size={11}/> Copy Link
            </button>
          </>
        )}
        <button onClick={() => setShowProfile(true)}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] transition"
                style={{ background: accent, color: "black" }}
                data-testid="theater-group-edit-profile-btn">
          <Quote size={11}/> Edit Bio & Press
        </button>
      </div>

      {/* PRODUCTIONS */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">Productions</div>
            <div className="font-display text-lg uppercase tracking-tight mt-1"
                 data-testid="theater-group-prod-heading">
              Your Slate {productions.length > 0 && <span className="text-white/40">· {productions.length}</span>}
            </div>
          </div>
          <Button className="h-10 rounded-full text-[11px] uppercase tracking-[0.22em]"
                  style={{ background: accent, color: "black" }}
                  onClick={() => setShowAdd(true)}
                  data-testid="theater-group-add-prod-btn">
            <Plus size={13} className="mr-1.5"/> Add Production
          </Button>
        </div>

        {loadingProds ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/50">
            <Loader2 size={16} className="animate-spin inline mr-2"/> loading…
          </div>
        ) : productions.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center" data-testid="theater-group-prod-empty">
            <Drama size={30} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No productions yet.</div>
            <div className="text-white/45 text-[11px] mt-2 max-w-md mx-auto">
              Add every play in your active repertoire so venues + audiences can see what you're offering.
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="theater-group-prod-list">
            {productions.map((p) => <ProductionCard key={p.id} prod={p} onDelete={refresh}/>)}
          </div>
        )}
      </div>

      {/* TILE GRID */}
      <div className="mt-12">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-3">Your Desks</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="theater-group-tiles">
          <Tile to="/theater/halls" TileIcon={Theater}
                title="Find a Venue"
                desc="Browse theater halls across India, request dates, book performances."
                accent={accent}/>
          <Tile to="/theater-group/bookings" TileIcon={Calendar}
                title="My Bookings"
                desc={`${bookings.count || 0} booking request${bookings.count === 1 ? "" : "s"} across all halls.`}
                accent={accent}/>
          <Tile to="/directory?talent_subtype=variety_performer" TileIcon={Users}
                title="Variety Artists"
                desc="Book comics, magicians, dancers, and solo musicians for your shows."
                accent={accent}/>
          <Tile to="/directory?role=main_talent" TileIcon={Users}
                title="Actors & Talent"
                desc="Search verified stage actors and performers by city, style, availability."
                accent={accent}/>
          <Tile to="/office/public-profile" TileIcon={Globe}
                title="Public Profile"
                desc="Your group's face for critics, funders, and the general public."
                accent={accent}/>
          <Tile to="/icard" TileIcon={IdCard}
                title="Owner iCard"
                desc="Your verified, co-branded identity card."
                accent={accent}/>
        </div>
      </div>

      <div className="mt-10">
        <OrgTeamPanel org={org}/>
      </div>

      {showAdd && (
        <AddProductionModal
          styleCatalog={styleCatalog}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}
      {showProfile && (
        <EditGroupProfileModal
          org={org}
          styleCatalog={styleCatalog}
          onClose={() => setShowProfile(false)}
          onSaved={(fresh) => { setShowProfile(false); setOrg(fresh); }}
        />
      )}
    </div>
  );
}

function ProductionCard({ prod, onDelete }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!confirm(`Remove "${prod.title}" from your slate?`)) return;
    setBusy(true);
    try {
      await api.delete(`/theater-group/productions/${prod.id}`);
      toast.success("Production removed.");
      onDelete();
    } catch {
      toast.error("Could not remove production.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cs-card rounded-xl p-5" data-testid={`theater-prod-${prod.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center"
             style={{ background: "#F9731622", color: "#F97316" }}>
          <Drama size={18}/>
        </div>
        <button onClick={remove} disabled={busy}
                className="text-white/40 hover:text-red-400 transition p-1 rounded-full"
                data-testid={`theater-prod-delete-${prod.id}`}>
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
        </button>
      </div>
      <div className="mt-3">
        <div className="font-display text-base uppercase tracking-tight">{prod.title}</div>
        <div className="text-[11px] text-white/60 mt-1">
          {prod.playwright && <span>by {prod.playwright}</span>}
          {prod.language && <span> · {prod.language}</span>}
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-white/8 grid grid-cols-2 gap-3 text-[11px] text-white/70">
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Opens</div>
          <div className="mt-0.5">{prod.opening_date || "TBA"}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Run · Cast</div>
          <div className="mt-0.5">{prod.running_time_min || "?"}min · {prod.cast_size || "?"}</div>
        </div>
      </div>
      {prod.synopsis && (
        <p className="mt-3 text-[11px] text-white/55 line-clamp-3 leading-relaxed">{prod.synopsis}</p>
      )}
    </div>
  );
}

function Tile({ to, TileIcon, title, desc, accent }) {
  return (
    <Link to={to} className="cs-card rounded-xl p-5 hover:border-white/25 transition-all block group"
          data-testid={`theater-group-tile-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center"
           style={{ background: `${accent}22`, color: accent }}>
        <TileIcon size={18}/>
      </div>
      <div className="mt-4">
        <div className="font-display text-lg uppercase tracking-tight">{title}</div>
        <p className="mt-1.5 text-[12px] text-white/60 leading-relaxed">{desc}</p>
      </div>
      <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.22em] uppercase text-white/60 group-hover:text-white">
          Open →
        </span>
        <ArrowRight size={13} className="text-white/40 group-hover:text-white group-hover:translate-x-0.5 transition"/>
      </div>
    </Link>
  );
}

function AddProductionModal({ styleCatalog, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", playwright: "", style: "", language: "",
    opening_date: "", running_time_min: 90, cast_size: 4,
    poster_url: "", synopsis: "", is_active: true,
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (form.title.trim().length < 1) { toast.error("Title is required."); return; }
    setBusy(true);
    try {
      await api.post("/theater-group/productions", {
        ...form,
        running_time_min: Number(form.running_time_min) || 0,
        cast_size: Number(form.cast_size) || 0,
      });
      toast.success("Production added.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add production.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="theater-group-add-prod-modal">
      <div className="w-full max-w-2xl cs-card rounded-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-500/15 text-orange-400 flex items-center justify-center">
            <Drama size={18}/>
          </div>
          <div>
            <div className="font-display text-lg uppercase tracking-tight">Add Production</div>
            <div className="text-[11px] text-white/55">Every play in your active repertoire.</div>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <Field label="Title *">
            <Input value={form.title} onChange={set("title")}
                   placeholder="e.g. Nagamandala"
                   className="bg-black/40 border-white/15"
                   data-testid="theater-prod-title"/>
          </Field>
          <Field label="Playwright">
            <Input value={form.playwright} onChange={set("playwright")}
                   placeholder="e.g. Girish Karnad"
                   className="bg-black/40 border-white/15"
                   data-testid="theater-prod-playwright"/>
          </Field>
          <Field label="Style">
            <select value={form.style} onChange={set("style")}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                    data-testid="theater-prod-style">
              <option value="">— pick —</option>
              {styleCatalog.map((t) => (<option key={t.key} value={t.key}>{t.label}</option>))}
            </select>
          </Field>
          <Field label="Language">
            <Input value={form.language} onChange={set("language")}
                   placeholder="Hindi / English / …"
                   className="bg-black/40 border-white/15"
                   data-testid="theater-prod-language"/>
          </Field>
          <Field label="Opens">
            <Input type="date" value={form.opening_date} onChange={set("opening_date")}
                   className="bg-black/40 border-white/15"
                   data-testid="theater-prod-open"/>
          </Field>
          <Field label="Running Time (min)">
            <Input type="number" min={0} max={600} value={form.running_time_min} onChange={set("running_time_min")}
                   className="bg-black/40 border-white/15"
                   data-testid="theater-prod-runtime"/>
          </Field>
          <Field label="Cast Size">
            <Input type="number" min={0} max={200} value={form.cast_size} onChange={set("cast_size")}
                   className="bg-black/40 border-white/15"
                   data-testid="theater-prod-cast"/>
          </Field>
          <Field label="Poster URL">
            <Input value={form.poster_url} onChange={set("poster_url")}
                   placeholder="https://…"
                   className="bg-black/40 border-white/15"
                   data-testid="theater-prod-poster"/>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Synopsis">
            <Textarea value={form.synopsis} onChange={set("synopsis")}
                      rows={4}
                      placeholder="A short one-para pitch for programmers & audiences…"
                      className="bg-black/40 border-white/15 min-h-[90px]"
                      data-testid="theater-prod-synopsis"/>
          </Field>
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="ghost"
                  className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5"
                  onClick={onClose}
                  data-testid="theater-prod-cancel">Cancel</Button>
          <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-orange-500 text-black hover:bg-orange-400 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="theater-prod-save">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Saving…</> : <>Save Production</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function EditGroupProfileModal({ org, styleCatalog, onClose, onSaved }) {
  const [bio, setBio] = useState(org.public_bio || "");
  const [hero, setHero] = useState(org.public_hero_image_url || "");
  const [styles, setStyles] = useState(new Set(org.theater_styles || []));
  const [quotes, setQuotes] = useState(
    (org.theater_group_press_quotes || []).length
      ? org.theater_group_press_quotes
      : [{ quote: "", source: "", date: "" }],
  );
  const [busy, setBusy] = useState(false);

  const toggleStyle = (k) => setStyles((s) => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const setQuote = (i, k) => (e) => setQuotes((qs) => qs.map((q, idx) =>
    idx === i ? { ...q, [k]: e.target.value } : q));
  const addQuote  = () => setQuotes((qs) => [...qs, { quote: "", source: "", date: "" }]);
  const dropQuote = (i) => setQuotes((qs) => qs.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true);
    try {
      const clean = quotes
        .map((q) => ({
          quote: (q.quote || "").trim(),
          source: (q.source || "").trim(),
          date: (q.date || "").trim(),
        }))
        .filter((q) => q.quote);
      await api.patch("/theater-group/mine", {
        bio: bio.trim(),
        hero_image_url: hero.trim(),
        theater_styles: Array.from(styles),
        press_quotes: clean,
      });
      const { data } = await api.get("/onboard/me/organization");
      toast.success("Public profile updated.");
      onSaved(data.organization);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="theater-group-edit-profile-modal">
      <div className="w-full max-w-2xl cs-card rounded-2xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-orange-500/15 text-orange-400 flex items-center justify-center">
            <Quote size={18}/>
          </div>
          <div>
            <div className="font-display text-lg uppercase tracking-tight">Edit Public Profile</div>
            <div className="text-[11px] text-white/55">Bio, styles, press quotes — shown to critics & funders.</div>
          </div>
        </div>

        <div className="space-y-5">
          <Field label="About your group">
            <Textarea rows={5} value={bio} onChange={(e) => setBio(e.target.value)}
                      maxLength={4000}
                      placeholder="Founded in… What draws you to theatre… Notable productions…"
                      className="bg-black/40 border-white/15 min-h-[110px]"
                      data-testid="theater-group-edit-bio"/>
            <div className="text-[10px] text-white/40 mt-1 text-right">{bio.length} / 4000</div>
          </Field>

          <Field label="Hero image URL (optional)">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={hero} onChange={(e) => setHero(e.target.value)}
                     placeholder="https://… or generate with AI"
                     className="bg-black/40 border-white/15 flex-1"
                     data-testid="theater-group-edit-hero"/>
              <HeroImageGenerator currentUrl={hero} onGenerated={(url) => setHero(url)} />
            </div>
            {hero && (
              <div className="mt-2 rounded-lg overflow-hidden border border-white/12"
                   data-testid="theater-group-hero-thumb">
                <img src={hero} alt="Hero preview" className="w-full max-h-44 object-cover"
                     onError={(e) => { e.currentTarget.style.display = "none"; }}/>
              </div>
            )}
          </Field>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Theatrical Styles</Label>
            <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="theater-group-edit-styles">
              {styleCatalog.map((s) => {
                const on = styles.has(s.key);
                return (
                  <button key={s.key} type="button" onClick={() => toggleStyle(s.key)}
                    className={`text-left px-3 py-2 rounded-lg border transition ${
                      on ? "border-white/45" : "bg-black/30 border-white/10 hover:bg-white/[0.05]"
                    }`}
                    style={on ? { borderColor: "#F97316", background: "#F9731615" } : {}}
                    data-testid={`theater-group-edit-style-${s.key}`}>
                    <div className="text-[12px] font-semibold text-white">{s.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Press Quotes</Label>
              <button onClick={addQuote}
                      className="text-[10px] tracking-[0.2em] uppercase text-orange-300 hover:text-orange-200"
                      data-testid="theater-group-edit-quote-add">
                <Plus size={10} className="inline mr-1"/> Add
              </button>
            </div>
            <div className="space-y-3">
              {quotes.map((q, i) => (
                <div key={i} className="cs-card rounded-lg p-3"
                     data-testid={`theater-group-edit-quote-${i}`}>
                  <Textarea rows={2} value={q.quote} onChange={setQuote(i, "quote")}
                            maxLength={1000}
                            placeholder='"Extraordinary…"'
                            className="bg-black/40 border-white/15 mb-2 text-[13px]"
                            data-testid={`theater-group-edit-quote-body-${i}`}/>
                  <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                    <Input value={q.source} onChange={setQuote(i, "source")}
                           placeholder="Source (The Hindu, Mid-Day…)"
                           className="bg-black/40 border-white/15 h-9 text-[12px]"
                           data-testid={`theater-group-edit-quote-source-${i}`}/>
                    <Input value={q.date} onChange={setQuote(i, "date")}
                           placeholder="2025-11"
                           className="bg-black/40 border-white/15 h-9 text-[12px] w-24"
                           data-testid={`theater-group-edit-quote-date-${i}`}/>
                    {quotes.length > 1 && (
                      <button onClick={() => dropQuote(i)}
                              className="text-white/40 hover:text-red-400 p-1"
                              data-testid={`theater-group-edit-quote-drop-${i}`}>
                        <Trash2 size={12}/>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="ghost"
                  className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5"
                  onClick={onClose}
                  data-testid="theater-group-edit-profile-cancel">Cancel</Button>
          <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-orange-500 text-black hover:bg-orange-400 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="theater-group-edit-profile-save">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Saving…</> : <>Save Public Profile</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
